import React, { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import axios from "api/axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PageShell, { SectionCard } from "components/patch/PageShell";
import PayrollRunPicker from "components/patch/PayrollRunPicker";
import SimpleTable from "components/patch/SimpleTable";

const money = (v) =>
  Number(v || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function normalizeRows(rows) {
  if (!rows) return [];
  if (Array.isArray(rows)) return rows;
  if (Array.isArray(rows.data)) return rows.data;
  return [];
}

export default function ReportPage({
  title,
  subtitle,
  endpoint,
  exportName,
  columns,
  summaryCards,
  requiresRun,
}) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [run, setRun] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildParams = (payload = selected, currentPage = page) => {
    const params = {
      per_page: perPage,
      page: currentPage,
    };

    const runId = payload?.runId || payload?.id || payload?.payroll_run_id;
    const month = payload?.month || payload?.value || payload?.payroll_month;

    if (runId) params.run_id = runId;
    else if (month) params.month = month;

    if (search) params.search = search;

    return params;
  };

  const load = async (payload = selected, currentPage = page) => {
    if (requiresRun && !payload) {
      setError("Please select a payroll run.");
      return;
    }

    setSelected(payload);
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(endpoint, {
        params: buildParams(payload, currentPage),
      });

      const data = res.data?.data || res.data || {};
      const paginated = data.rows || {};

      setRun(data.run || data.selected_run || null);
      setSummary(data.summary || data.payroll || data.staff || {});

      setRows(normalizeRows(paginated));

      setPage(paginated.current_page || 1);
      setLastPage(paginated.last_page || 1);
      setTotal(paginated.total || 0);
    } catch (err) {
      setRows([]);
      setSummary({});
      setError(
        err.response?.data?.message ||
          "Unable to load report. Please confirm that payroll has been generated."
      );
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const params = new URLSearchParams(buildParams()).toString();
    const baseURL = axios.defaults.baseURL || "";
    window.open(`${baseURL}/reports/export/${exportName}.csv?${params}`, "_blank");
  };

  return (
    <PageShell title={title} subtitle={subtitle}>
      {requiresRun && (
        <SectionCard title="Pick Payroll Run">
          <PayrollRunPicker onSelected={load} />
        </SectionCard>
      )}

      <MDBox mt={3} display="flex" gap={2} flexWrap="wrap" alignItems="center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search PFNO, name..."
          style={{
            height: 42,
            minWidth: 280,
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: "0 14px",
            fontSize: 14,
          }}
        />

        <select
          value={perPage}
          onChange={(e) => {
            setPerPage(Number(e.target.value));
            setPage(1);

            setTimeout(() => {
              load(selected, 1);
            }, 0);
          }}
          style={{
            height: 42,
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: "0 14px",
          }}
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={500}>500</option>
        </select>

        <Button variant="contained" color="primary" onClick={() => load()}>
          Refresh
        </Button>

        <Button variant="outlined" color="primary" onClick={exportCsv}>
          Export CSV
        </Button>
      </MDBox>

      {loading && (
        <MDBox mt={3} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <MDTypography variant="button">Loading report...</MDTypography>
        </MDBox>
      )}

      {error && (
        <MDBox mt={3}>
          <Alert severity="error">{error}</Alert>
        </MDBox>
      )}

      {run && (
        <MDBox mt={3}>
          <Alert severity="info">
            Selected Run: {run.month} | Status: {run.status || "N/A"}
          </Alert>
        </MDBox>
      )}

      {summaryCards.length > 0 && (
        <MDBox mt={3}>
          <Grid container spacing={3}>
            {summaryCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.key}>
                <Card sx={{ borderRadius: 3, height: "100%" }}>
                  <MDBox p={3}>
                    <MDTypography variant="button" color="text">
                      {card.label}
                    </MDTypography>
                    <MDTypography variant="h4" fontWeight="bold">
                      {card.money ? `₦${money(summary?.[card.key])}` : summary?.[card.key] || 0}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        </MDBox>
      )}

      <MDBox mt={3}>
        <SectionCard title="Report Result">
          <SimpleTable columns={columns} rows={rows} />
          <MDBox
            mt={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <MDTypography variant="button">
              Showing page {page} of {lastPage} | Total Records: {total}
            </MDTypography>

            <MDBox display="flex" gap={1}>
              <Button
                variant="outlined"
                disabled={page <= 1}
                onClick={() => load(selected, page - 1)}
              >
                Previous
              </Button>

              <Button
                variant="contained"
                disabled={page >= lastPage}
                onClick={() => load(selected, page + 1)}
              >
                Next
              </Button>
            </MDBox>
          </MDBox>
        </SectionCard>
      </MDBox>
    </PageShell>
  );
}

ReportPage.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  endpoint: PropTypes.string.isRequired,
  exportName: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  summaryCards: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      money: PropTypes.bool,
    })
  ),
  requiresRun: PropTypes.bool,
};

ReportPage.defaultProps = {
  subtitle: "",
  summaryCards: [],
  requiresRun: true,
};
